import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import {
  every,
  find,
  includes,
  isEmpty,
  isEqual,
  isNil,
  join,
  keys,
  map,
  split
} from 'lodash/fp'
import {
  ArrayInput,
  Loading,
  SimpleForm,
  SimpleFormIterator,
  useGetOne,
  useNotify,
  useTranslate,
  useUpdate
} from 'react-admin'
import { Path, useFormContext, useFormState, useWatch } from 'react-hook-form'
import {
  InquiryItem,
  type ContactItem
} from '../../../../cdk/ops-contact-form/lib/common/types'
import { useParams } from 'react-router-dom'
import { MuiFileInput } from 'mui-file-input'
import { TextInput, TextShow } from 'components/Util'
import { useEffect, useState } from 'react'
import log from 'loglevel'
import { format } from 'date-fns/fp'
import { assign } from 'lodash'
import { CloudUploadOutlined } from '@mui/icons-material'
import axios from 'axios'
import { url } from 'components/DataProvider/functions'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'

const accessToken = 'token'

const InquiryItemsSchema = z.object({
  mainCategory: z.string().optional(),
  subCategory: z.string().optional(),
  detail: z.string().optional(),
  note: z.string().optional(),
  response: z.string()
})

const DenkankyoStatusSchema = z.enum(['申請前', '申請後'])

const ContactFormSchema = z.object({
  id: z.string().optional(),
  partner: z.string().optional(),
  organization: z.string().optional(),
  product: z.string().optional(),
  issueKey: z.string().optional(),
  entryDate: z.string().optional(),
  address: z.string().optional(),
  place: z.string().optional(),
  placeNumber: z.string().optional(),
  attributeNumber: z.string().optional(),
  assigneeName: z.string().optional(),
  assigneeEmail: z.string().optional(),
  denkankyoStatus: DenkankyoStatusSchema.optional(),
  inquiryItems: z.array(InquiryItemsSchema),
  contactDate: z.string().optional(),
  responseDate: z.string().optional(),
  reUpload: z.boolean().optional(),
  deleted: z.boolean()
})

const ResponseForm: React.FC = (): JSX.Element => {
  const translation = useTranslate()
  const { id } = useParams()
  if (isNil(id)) return <></>
  // const isDesktop = useMediaQuery('(min-width: 900px)')
  const [open, setOpen] = useState<boolean>(false)
  const [uploadFiles, setUploadFiles] = useState<File[] | undefined>(undefined)
  const [fileNames, setFileNames] = useState<string>('')
  const [uploadFileIsImage, setUploadFileIsImage] = useState<boolean>(false)
  const [updated, setUpdated] = useState<boolean>(false)
  const [submit, setSubmit] = useState<boolean>(false)
  const { isLoading: isLoadingContactItem, data: contactItem } =
    useGetOne<ContactItem>('contact', { id })
  useEffect(() => {
    if (isNil(uploadFiles)) return
    setFileNames(join(',')(uploadFiles.map((file) => file.name)))
  }, [uploadFiles])
  if (isNil(contactItem)) return <></>
  if (isLoadingContactItem) return <Loading />
  if (submit) return <Loading />
  const handleInput = (uploadFile: File[] | undefined): void => {
    setUploadFiles(uploadFile)
    const allowedFileTypes = [
      'image/png',
      'image/jpeg',
      'image/gif',
      'application/pdf'
    ]
    if (
      !isNil(uploadFile) &&
      every((file: File) => includes(file.type)(allowedFileTypes))(uploadFile)
    ) {
      setUploadFileIsImage(true)
    } else {
      setUploadFileIsImage(false)
    }
  }
  const handleClickOpen = (): void => {
    setOpen(true)
  }
  const handleClose = (): void => {
    setUploadFiles(undefined)
    setOpen(false)
  }
  const { denkankyoStatus } = contactItem
  return (
    <Grid
      container
      display="flex"
      justifyContent="center"
      alignContent="center">
      <Box display="grid" justifyContent="center" marginY={5}>
        <Box display="grid" justifyContent="center" marginY={1}>
          <Typography
            variant="h6"
            sx={{
              whiteSpace: 'pre-line'
            }}>
            {'テラシテRサービス'}
          </Typography>
        </Box>
        <Box display="grid" justifyContent="center" marginY={1}>
          <Typography
            variant="caption"
            sx={{
              whiteSpace: 'pre-line'
            }}>
            {
              '平素よりお世話になります、中部電力ミライズコネクト　テラシテR事務局でございます。\r\n先日お申込みいただいた以下物件の内容におきまして、回答を頂きたくご連絡します。'
            }
          </Typography>
        </Box>
        <Box display="grid" justifyContent="center" marginY={1}>
          <Typography
            variant="caption"
            sx={{
              borderBottom: 2,
              borderColor: 'GrayText',
              // color: 'gray',
              whiteSpace: 'pre-line'
            }}>
            {'回答情報のご入力'}
          </Typography>
        </Box>
        <SimpleForm
          resource="resources.contact.fields"
          resolver={zodResolver(ContactFormSchema)}
          record={contactItem}
          toolbar={false}>
          <Box display="flex">
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="issueKey"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="contactDate"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
            <Box marginX={'1em'}>
              <TextShow label="ステータス">{denkankyoStatus}</TextShow>
            </Box>
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="attributeNumber"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
          </Box>
          <Box display="flex">
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="place"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="placeNumber"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
            <Box marginX={'1em'}>
              <TextInput
                fields="resources.contact.fields"
                source="address"
                placeholder={''}
                disabled
                fullWidth
              />
            </Box>
          </Box>
          <Box
            display="flex"
            marginX={'0.5em'}
            marginY={'2em'}
            sx={{
              width: '100%'
            }}>
            <ArrayInput source="inquiryItems" label={''} fullWidth>
              <SimpleFormIterator
                disableAdd
                disableClear
                disableRemove
                disableReordering
                inline
                fullWidth>
                <InquiryItemsInput setUpdated={setUpdated} />
              </SimpleFormIterator>
            </ArrayInput>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            marginX={'0.5em'}
            marginY={'2em'}>
            <Button
              variant="contained"
              onClick={handleClickOpen}
              sx={{ borderRadius: '10px' }}>
              <CloudUploadOutlined />
              {`ファイルのアップロード`}
            </Button>
            <Typography
              variant="h6"
              sx={{
                color: 'gray'
              }}>
              {fileNames ?? ''}
            </Typography>
          </Box>
          <SubmitButton
            record={contactItem}
            setRecord={() => {}}
            files={uploadFiles}
            updated={updated}
            setFiles={setUploadFiles}
            setSubmit={setSubmit}
          />
        </SimpleForm>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            // handleClose()
          }
        }}>
        <DialogTitle>{`ファイルのアップロード`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`添付ファイルを選択してください。`}
          </DialogContentText>
          <MuiFileInput
            value={uploadFiles ?? []}
            onChange={handleInput}
            variant="outlined"
            multiple
          />
          <br />
          <Typography
            variant="caption"
            component="div"
            gutterBottom>{`pdf,jpg,png,gifファイルのみ、ファイルサイズは5MB以内。`}</Typography>
          {uploadFiles == null && (
            <Typography
              variant="caption"
              component="div"
              color="error.main"
              gutterBottom>{`ファイルを選択してください。`}</Typography>
          )}
          {uploadFiles != null && !uploadFileIsImage && (
            <Typography
              variant="caption"
              component="div"
              color="error.main"
              gutterBottom>{`このファイルタイプはサポートしていません。`}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>キャンセル</Button>
          <Button
            onClick={async () => {
              setOpen(false)
            }}
            disabled={
              isNil(uploadFiles) || !uploadFileIsImage
            }>{`決定`}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}
const InquiryItemsInput: React.FC<{
  source?: string
  setUpdated: (updated: boolean) => void
}> = ({ setUpdated, ...props }): JSX.Element => {
  const { source } = props
  const { setValue } = useFormContext<ContactItem>()
  const [resourceName, indexStr] = split('.')(source)
  if (isNil(resourceName) || isNil(indexStr)) {
    return <></>
  }
  const path = `inquiryItems.${indexStr}.response` as Path<ContactItem>
  const handleResponse = (event: any) => {
    setUpdated(true)
    setValue(path, event.target.value)
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <TextInput
          fields={`resources.contact.fields`}
          source={`${source}.mainCategory`}
          placeholder={''}
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          fields={`resources.contact.fields`}
          source={`${source}.note`}
          placeholder={''}
          disabled
          multiline={true}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          fields={`resources.contact.fields`}
          source={`${source}.response`}
          placeholder={''}
          multiline
          fullWidth
          onChange={handleResponse}
        />
      </Grid>
    </Grid>
  )
}
const SubmitButton: React.FC<{
  record: ContactItem
  setRecord: (data: ContactItem) => void
  files: File[] | undefined
  updated: boolean
  setFiles: (files: File[] | undefined) => void
  setSubmit: (submit: boolean) => void
}> = ({
  record,
  setRecord,
  files,
  updated,
  setFiles,
  setSubmit
}): JSX.Element => {
  const { getValues } = useFormContext<ContactItem>()
  const formValues = useWatch()
  const [enable, setEnable] = useState(false)
  const [data, setData] = useState<Partial<ContactItem>>({})
  const translate = useTranslate()
  const { errors } = useFormState()
  const notify = useNotify()
  const [update] = useUpdate(undefined, undefined, {
    onSettled: (data: ContactItem | undefined) => {
      if (!isNil(data)) setRecord(data)
    }
  })
  const values = getValues()
  const { inquiryItems: inputInquiryItems } = values
  const { inquiryItems } = record
  // @ts-expect-error ts(2339)
  const currentInquiryItems = map.convert({ cap: false })(
    (item: InquiryItem, index: number) => {
      const { response } = item
      return {
        ...inquiryItems[index],
        response
      }
    }
  )(inputInquiryItems)
  const onSubmit = (): void => {
    setSubmit(true)
    const { id, issueKey } = record
    setEnable(false)
    map(async (file: File) => {
      const res = await axios.get(`${url as string}/url/file/upload`, {
        params: {
          id,
          backlogIssueKey: issueKey,
          fileName: file.name
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(accessToken)}`
        }
      })
      if (!isNil(res) && !isNil(res.data) && res.status === 200) {
        const signedUrl = res.data
        const put = await axios.put(`${signedUrl as string}`, file)
        if (isNil(put) || put.status !== 200) {
          notify('resources.contact.notify.file.error', { type: 'error' })
        }
      } else {
        notify('resources.contact.notify.file.error', { type: 'error' })
      }
    })(files)
    if (!isEmpty(data)) {
      const today = new Date()
      update('contact', {
        id,
        data: { ...data, responseDate: format('yyyy-MM-dd')(today) },
        previousData: record
      }).catch((_e) => {
        // log.error(e)
        notify('resources.contact.notify.submit.error', { type: 'error' })
      })
    }
    notify('resources.contact.notify.submit.success', { type: 'success' })
    setFiles(undefined)
    setSubmit(false)
  }
  useEffect(() => {
    // const currentInquiryItems = values.inquiryItems
    if (isEqual(record.inquiryItems)(currentInquiryItems)) {
      setEnable(false)
    }
    if (isEmpty(errors)) {
      const updateInput: Partial<ContactItem> = {}
      if (!isEqualArray(inquiryItems, currentInquiryItems)) {
        assign(updateInput, { ...record, inquiryItems: currentInquiryItems })
      }
      setEnable(
        updated &&
          isEmpty(
            find((item: InquiryItem) => isEmpty(item.response))(
              currentInquiryItems
            )
          )
      )
      if (!isEmpty(updateInput)) {
        setData(updateInput)
      }
    }
  }, [errors, formValues, files])
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      // fullWidth
      disabled={!enable}
      onClick={onSubmit}>
      <Typography variant="button">
        {translate(`resources.contact.button.submit`)}
      </Typography>
    </Button>
  )
}
const isEqualArray = (array1: any[], array2: any[]): boolean => {
  const str1 = JSON.stringify(
    map((item: any) => {
      map((key: keyof typeof item) => {
        if (isNil(item[key])) item[key] = ''
      })(keys(item) as Array<keyof typeof item>)
      return item
    })(array1)
  )
  const str2 = JSON.stringify(
    map((item: any) => {
      map((key: keyof typeof item) => {
        if (isNil(item[key])) item[key] = ''
      })(keys(item) as Array<keyof typeof item>)
      return item
    })(array2)
  )
  return str1 === str2
}

export default ResponseForm
